import React, { useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DateRangeStyled } from '../Global/Styling';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { fDate } from '../../utils/formatTime';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

export default function AgreementToolbar({
  filterName,
  setFilterName,
  parent = 'agreement',
  filterDatesRange,
  setFilterDatesRange,
  selectStatus,
  selectStatusValue,
  setSelectStatusValue,
  setFilterParameter,
  filterParameter
}) {
  const navigate = useNavigate();
  const [isOpenFilter, setOpenFilter] = useState(false);
  const { BankDrop } = useGlobalSelector();

  const handleRemoveFilter = () => {
    setFilterDatesRange([
      {
        startDate: null,
        endDate: null,
        key: 'selection'
      }
    ]);
    setSelectStatusValue(' ');
    setFilterName('');
    setFilterParameter({
      bankId: null
    });
  };

  const onFilterName = (event) => {
    setFilterName(event.target.value);
  };

  return (
    <>
      {/* Top Toolbar Row */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {/* Left Side: + Add Button */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Add New Agreement" arrow>
            <Button
              variant="contained"
              onClick={() => {
                localStorage.removeItem('reviewData');
                localStorage.removeItem('commisssionType');
                navigate(`/${parent}/add`);
              }}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                p: 0,
                bgcolor: '#0284c7',
                color: '#ffffff',
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)',
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              <AddIcon sx={{ fontSize: 22 }} />
            </Button>
          </Tooltip>
        </Box>

        {/* Right Side: Search and Filter Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Agreement..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260, md: 290 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': {
                  borderColor: '#e2e8f0'
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e1'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0284c7'
                }
              }
            }}
          />

          <Tooltip title={isOpenFilter ? 'Hide Filters' : 'Show Filters'} arrow>
            <IconButton
              onClick={() => setOpenFilter((prev) => !prev)}
              sx={{
                width: 40,
                height: 40,
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.08)' : '#ffffff',
                color: '#0284c7',
                '&:hover': {
                  borderColor: '#0284c7',
                  bgcolor: 'rgba(2, 132, 199, 0.08)'
                }
              }}
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Expandable Filters Section */}
      {isOpenFilter && (
        <Box
          sx={{
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Bank Select */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                disablePortal
                id="combo-box-bank"
                options={BankDrop || []}
                size="small"
                getOptionLabel={(option) => option.label || ''}
                renderOption={(props, option) => <li {...props}>{option.label}</li>}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select Bank Name"
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13
                      },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                    }}
                  />
                )}
                value={BankDrop?.find((item) => item.value === filterParameter?.bankId) || null}
                onChange={(e, newValue) => {
                  setFilterParameter((pre) => ({
                    ...pre,
                    bankId: newValue?.value
                  }));
                }}
              />
            </Grid>

            {/* Date Range Picker */}
            <Grid item xs={12} sm={6} md={4} sx={{ position: 'relative' }}>
              <Box
                className="displayDateRange"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 24,
                  zIndex: 1,
                  pointerEvents: 'none',
                  fontSize: 13,
                  color:
                    filterDatesRange[0].startDate || filterDatesRange[0].endDate
                      ? '#1e293b'
                      : '#94a3b8'
                }}
              >
                {filterDatesRange[0].startDate !== null
                  ? `${fDate(filterDatesRange[0].startDate)} to `
                  : ''}
                {filterDatesRange[0].endDate !== null ? fDate(filterDatesRange[0].endDate) : ''}
                {filterDatesRange[0].startDate === null && filterDatesRange[0].endDate === null ? (
                  <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Select Date Range</Typography>
                ) : (
                  ''
                )}
              </Box>
              <Select
                fullWidth
                size="small"
                value=" "
                className="datesSelect"
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <DateRangeStyled
                  editableDateInputs={false}
                  onChange={(item) => setFilterDatesRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={filterDatesRange}
                  className="dateRangePicker"
                />
              </Select>
            </Grid>

            {/* Status Select */}
            <Grid item xs={12} sm={6} md={3}>
              <Select
                fullWidth
                size="small"
                value={selectStatusValue}
                onChange={(e) => {
                  setSelectStatusValue(e.target.value);
                }}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '8px',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" " disabled sx={{ fontSize: 13 }}>
                  Select Status
                </MenuItem>
                <MenuItem value={selectStatus.ACTIVE.type_id} sx={{ fontSize: 13 }}>
                  Active
                </MenuItem>
                <MenuItem value={selectStatus.INACTIVE.type_id} sx={{ fontSize: 13 }}>
                  Inactive
                </MenuItem>
                <MenuItem value={selectStatus.INCOMPLETE.type_id} sx={{ fontSize: 13 }}>
                  Incomplete
                </MenuItem>
              </Select>
            </Grid>

            {/* Reset Filter Button */}
            <Grid item xs={12} sm={6} md={1}>
              <Tooltip title="Reset Filter" arrow>
                <IconButton
                  onClick={handleRemoveFilter}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(239, 68, 68, 0.08)',
                    color: '#ef4444',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.16)' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}
